/**
 * Simulated flight. A scripted aircraft publishes PositionSamples through
 * parsePositionSample — the same parser native SimConnect uses — so the desk,
 * the phone, and a future EFB all read one live fix. No MSFS process.
 * Refuses a career that is not marked TEST.
 */
import type { CharterRequest } from "./types";
import { DeveloperGuardError, assertTestCareer, buildTestCareer, type SkipPhase } from "./developerTools";
import { MOCK_GROK_INBOX, mockGrokEnabled, mockGrokFetch, setMockGrokEnabled } from "./developerMocks";
import { aircraftSimState, deriveFlightPhase, type FlightPhaseId } from "./flightPhase";
import {
  fusePosition,
  hydrateFusedPosition,
  liveFixToken,
  parsePositionSample,
  type FetchSimConnectResult,
  type FusedPosition,
  type PositionSample,
} from "./position";
import { deskScheduleFingerprint } from "./liveDeskSync";
import { liveFingerprint, liveSliceFromSnapshot, type SyncLiveSlice } from "./syncRevision";
import { useDispatchStore } from "./store";

export const SIM_SPEED_MIN = 1;
export const SIM_SPEED_MAX = 60;

const CYPD = { lat: 45.6567, lon: -61.3681, alt: 377 };
const CYUL = { lat: 45.4706, lon: -73.7408, alt: 118 };
const CYHZ = { lat: 44.8808, lon: -63.5086, alt: 477 };

export type SimKeyframe = {
  tMin: number;
  phase: SkipPhase;
  lat: number;
  lon: number;
  altFt: number;
  gsKt: number;
  vsFpm: number;
  iasKt: number;
  fuelLb: number;
  gearDown: boolean;
  onGround: boolean;
  n1: number;
};

export type SimFlight = {
  id: string;
  title: string;
  summary: string;
  registration: "C-GNSH";
  origin: string;
  destination: string;
  divertedTo?: string;
  frames: readonly SimKeyframe[];
};

export type SimFlightState = {
  flightId: string;
  speed: number;
  paused: boolean;
  elapsedMs: number;
  lastRealMs: number | null;
};

type Frame = SimKeyframe;

function frame(partial: Frame): Frame {
  return partial;
}

const CHARTER_FRAMES: readonly Frame[] = [
  frame({ tMin: 0, phase: "preflight", lat: CYPD.lat, lon: CYPD.lon, altFt: CYPD.alt, gsKt: 0, vsFpm: 0, iasKt: 0, fuelLb: 3200, gearDown: true, onGround: true, n1: 0 }),
  frame({ tMin: 10, phase: "taxi", lat: 45.6605, lon: -61.36, altFt: CYPD.alt, gsKt: 24, vsFpm: 0, iasKt: 0, fuelLb: 3188, gearDown: true, onGround: true, n1: 42 }),
  frame({ tMin: 16, phase: "takeoff", lat: 45.668, lon: -61.34, altFt: CYPD.alt, gsKt: 120, vsFpm: 200, iasKt: 125, fuelLb: 3160, gearDown: true, onGround: true, n1: 94 }),
  frame({ tMin: 20, phase: "takeoff", lat: 45.69, lon: -61.5, altFt: 650, gsKt: 180, vsFpm: 1800, iasKt: 170, fuelLb: 3080, gearDown: true, onGround: false, n1: 92 }),
  frame({ tMin: 36, phase: "cruise", lat: 45.55, lon: -68, altFt: 41000, gsKt: 430, vsFpm: 0, iasKt: 250, fuelLb: 2740, gearDown: false, onGround: false, n1: 82 }),
  frame({ tMin: 68, phase: "descent", lat: 45.5, lon: -73, altFt: 16000, gsKt: 320, vsFpm: -1400, iasKt: 240, fuelLb: 2360, gearDown: false, onGround: false, n1: 55 }),
  frame({ tMin: 80, phase: "descent", lat: 45.48, lon: -73.6, altFt: 3000, gsKt: 170, vsFpm: -800, iasKt: 160, fuelLb: 2260, gearDown: true, onGround: false, n1: 48 }),
  frame({ tMin: 86, phase: "landed", lat: CYUL.lat, lon: CYUL.lon, altFt: CYUL.alt, gsKt: 60, vsFpm: -200, iasKt: 60, fuelLb: 2220, gearDown: true, onGround: true, n1: 40 }),
  frame({ tMin: 92, phase: "on-blocks", lat: 45.467, lon: -73.745, altFt: CYUL.alt, gsKt: 0, vsFpm: 0, iasKt: 0, fuelLb: 2204, gearDown: true, onGround: true, n1: 0 }),
];

function shifted(frames: readonly Frame[], addMin: number, fuelDelta: number): Frame[] {
  return frames.map((row) => ({ ...row, tMin: row.tMin + addMin, fuelLb: row.fuelLb + fuelDelta }));
}

const FERRY_FRAMES: readonly Frame[] = [
  frame({ tMin: 0, phase: "preflight", lat: CYUL.lat, lon: CYUL.lon, altFt: CYUL.alt, gsKt: 0, vsFpm: 0, iasKt: 0, fuelLb: 2600, gearDown: true, onGround: true, n1: 0 }),
  frame({ tMin: 8, phase: "taxi", lat: 45.474, lon: -73.73, altFt: CYUL.alt, gsKt: 22, vsFpm: 0, iasKt: 0, fuelLb: 2584, gearDown: true, onGround: true, n1: 40 }),
  frame({ tMin: 14, phase: "takeoff", lat: 45.49, lon: -73.7, altFt: CYUL.alt, gsKt: 118, vsFpm: 180, iasKt: 120, fuelLb: 2550, gearDown: true, onGround: true, n1: 94 }),
  frame({ tMin: 18, phase: "takeoff", lat: 45.52, lon: -73.4, altFt: 650, gsKt: 190, vsFpm: 1700, iasKt: 175, fuelLb: 2480, gearDown: true, onGround: false, n1: 90 }),
  frame({ tMin: 40, phase: "cruise", lat: 45.55, lon: -68, altFt: 39000, gsKt: 420, vsFpm: 0, iasKt: 245, fuelLb: 2100, gearDown: false, onGround: false, n1: 80 }),
  frame({ tMin: 70, phase: "descent", lat: 45.6, lon: -63.2, altFt: 14000, gsKt: 310, vsFpm: -1300, iasKt: 230, fuelLb: 1880, gearDown: false, onGround: false, n1: 52 }),
  frame({ tMin: 82, phase: "descent", lat: 45.64, lon: -61.7, altFt: 2800, gsKt: 165, vsFpm: -750, iasKt: 155, fuelLb: 1800, gearDown: true, onGround: false, n1: 46 }),
  frame({ tMin: 88, phase: "landed", lat: CYPD.lat, lon: CYPD.lon, altFt: CYPD.alt, gsKt: 58, vsFpm: -180, iasKt: 58, fuelLb: 1764, gearDown: true, onGround: true, n1: 38 }),
  frame({ tMin: 94, phase: "on-blocks", lat: 45.655, lon: -61.372, altFt: CYPD.alt, gsKt: 0, vsFpm: 0, iasKt: 0, fuelLb: 1750, gearDown: true, onGround: true, n1: 0 }),
];

const DELAY_FRAMES: readonly Frame[] = [
  frame({ ...CHARTER_FRAMES[0], tMin: 0, fuelLb: 3200 }),
  frame({ ...CHARTER_FRAMES[0], tMin: 30, fuelLb: 3188 }),
  ...shifted(CHARTER_FRAMES.slice(1), 30, -12),
];

const DIVERSION_FRAMES: readonly Frame[] = [
  ...CHARTER_FRAMES.slice(0, 5),
  frame({ tMin: 52, phase: "cruise", lat: 45.4, lon: -70.2, altFt: 41000, gsKt: 430, vsFpm: 0, iasKt: 250, fuelLb: 2580, gearDown: false, onGround: false, n1: 82 }),
  frame({ tMin: 60, phase: "descent", lat: 45.1, lon: -66.4, altFt: 18000, gsKt: 340, vsFpm: -1600, iasKt: 230, fuelLb: 2460, gearDown: false, onGround: false, n1: 54 }),
  frame({ tMin: 74, phase: "descent", lat: 44.95, lon: -64.1, altFt: 4000, gsKt: 180, vsFpm: -900, iasKt: 165, fuelLb: 2320, gearDown: true, onGround: false, n1: 48 }),
  frame({ tMin: 82, phase: "landed", lat: CYHZ.lat, lon: CYHZ.lon, altFt: CYHZ.alt, gsKt: 55, vsFpm: -200, iasKt: 55, fuelLb: 2260, gearDown: true, onGround: true, n1: 40 }),
  frame({ tMin: 88, phase: "on-blocks", lat: 44.878, lon: -63.5, altFt: CYHZ.alt, gsKt: 0, vsFpm: 0, iasKt: 0, fuelLb: 2248, gearDown: true, onGround: true, n1: 0 }),
];

const FLIGHTS: readonly SimFlight[] = [
  {
    id: "c-gnsh-charter",
    title: "C-GNSH charter CYPD–CYUL",
    summary: "Hawker charter. Ferry is a separate flight. Fuel and gear follow the script.",
    registration: "C-GNSH",
    origin: "CYPD",
    destination: "CYUL",
    frames: CHARTER_FRAMES,
  },
  {
    id: "c-gnsh-ferry",
    title: "C-GNSH ferry CYUL–CYPD",
    summary: "Empty ferry back to Port Hawkesbury. Lower start fuel.",
    registration: "C-GNSH",
    origin: "CYUL",
    destination: "CYPD",
    frames: FERRY_FRAMES,
  },
  {
    id: "c-gnsh-delay",
    title: "C-GNSH charter, delayed departure",
    summary: "Thirty minutes on the ramp at CYPD, then the same CYPD–CYUL charter.",
    registration: "C-GNSH",
    origin: "CYPD",
    destination: "CYUL",
    frames: DELAY_FRAMES,
  },
  {
    id: "c-gnsh-diversion",
    title: "C-GNSH charter CYPD–CYUL, divert CYHZ",
    summary: "Leaves for Montreal, then turns and lands at Halifax.",
    registration: "C-GNSH",
    origin: "CYPD",
    destination: "CYUL",
    divertedTo: "CYHZ",
    frames: DIVERSION_FRAMES,
  },
];

const ALIASES: Record<string, string> = {
  "c-gnsh-cypd-cyul": "c-gnsh-charter",
};

export function clampSimSpeed(speed: number): number {
  if (!Number.isFinite(speed)) return SIM_SPEED_MIN;
  return Math.min(SIM_SPEED_MAX, Math.max(SIM_SPEED_MIN, Math.round(speed)));
}

export function listSimFlights(): readonly SimFlight[] {
  return FLIGHTS;
}

export function resolveSimFlight(id: string): SimFlight | null {
  const key = ALIASES[id] ?? id;
  return FLIGHTS.find((flight) => flight.id === key) ?? null;
}

export function requireSimFlight(id: string): SimFlight {
  const flight = resolveSimFlight(id);
  if (!flight) throw new Error(`Unknown simulated flight "${id}".`);
  return flight;
}

export function simFlightDurationMs(flight: SimFlight): number {
  const last = flight.frames[flight.frames.length - 1];
  return (last?.tMin ?? 0) * 60_000;
}

export function assertSimulatedFlightCareer(career: { test?: boolean } | null | undefined): void {
  assertTestCareer(career, "Simulated flight");
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function bearingDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const deg = (Math.atan2(y, x) * 180) / Math.PI;
  if (!Number.isFinite(deg)) return 0;
  return (deg + 360) % 360;
}

function segment(flight: SimFlight, elapsedMs: number): { index: number; left: Frame; right: Frame; t: number } {
  const frames = flight.frames;
  const minutes = Math.max(0, Math.min(elapsedMs, simFlightDurationMs(flight))) / 60_000;
  let index = 0;
  while (index < frames.length - 1 && (frames[index + 1]?.tMin ?? 0) <= minutes) index += 1;
  const left = frames[index] ?? frames[0];
  const right = frames[Math.min(index + 1, frames.length - 1)] ?? left;
  if (!left || !right) throw new Error(`Simulated flight ${flight.id} has no frames.`);
  const span = right.tMin - left.tMin;
  const t = span <= 0 ? 0 : Math.max(0, Math.min(1, (minutes - left.tMin) / span));
  return { index, left, right, t };
}

export function phaseAt(flight: SimFlight, elapsedMs: number): SkipPhase {
  return segment(flight, elapsedMs).left.phase;
}

/** Parsed SimConnect sample at a script time. Gear and phase come from the current keyframe. */
export function sampleAt(flight: SimFlight, elapsedMs: number, now = new Date()): PositionSample {
  const { index, left, right, t } = segment(flight, elapsedMs);
  const frames = flight.frames;
  const from = index >= frames.length - 1 ? frames[Math.max(0, index - 1)] ?? left : left;
  const to = index >= frames.length - 1 ? left : right;
  const lat = lerp(left.lat, right.lat, t);
  const lon = lerp(left.lon, right.lon, t);
  const alt = Math.round(lerp(left.altFt, right.altFt, t));
  const agl = left.onGround ? 0 : Math.max(50, alt - 300);
  const fuelLb = Math.round(lerp(left.fuelLb, right.fuelLb, t));
  const n1 = Math.round(lerp(left.n1, right.n1, t));
  const parsed = parsePositionSample(
    {
      lat,
      lon,
      heading: Math.round(bearingDeg(from.lat, from.lon, to.lat, to.lon)),
      altitudeFt: alt,
      altitudeAgl: agl,
      airspeed: Math.round(lerp(left.iasKt, right.iasKt, t)),
      groundSpeed: Math.round(lerp(left.gsKt, right.gsKt, t)),
      verticalSpeedFpm: Math.round(lerp(left.vsFpm, right.vsFpm, t)),
      onGround: left.onGround,
      gearDown: left.gearDown,
      fuelQtyLb: fuelLb,
      fuelTotalGal: Math.round((fuelLb / 6.7) * 10) / 10,
      eng1N1: n1,
      eng2N1: n1,
      eng1Combustion: n1 >= 40,
      eng2Combustion: n1 >= 40,
      squawk: 4261,
      atUtc: now.toISOString(),
    },
    now,
  );
  if (!parsed) throw new Error(`Simulated flight ${flight.id} produced no position.`);
  return parsed;
}

/**
 * The desk live feed. Native SimConnect and a simulated flight both end here:
 * `parsePositionSample`, then `fusePosition`, then `lastKnownPosition` on `/v1/live`.
 * The phone and the tablet EFB hydrate that object. They do not read a side bus.
 */
export function fuseSimulatedSample(sample: PositionSample | null, now = new Date()): FusedPosition | null {
  const polled = simulatedConnectSample(sample, now);
  if (!polled.ok) return null;
  const fused = fusePosition({ simconnect: polled.sample, now });
  if (!fused || fused.source !== "simconnect" || fused.stale) return null;
  return fused;
}

/** `/v1/live` slice an EFB reads. `lastKnownPosition` is the SimConnect-shaped fix. */
export function simConnectFeedFromDesk(desk: {
  lastKnownPosition?: unknown;
  crewPresence?: unknown;
  flightTracks?: unknown;
  exportedAtUtc?: string;
}): SyncLiveSlice {
  return liveSliceFromSnapshot({
    exportedAtUtc: desk.exportedAtUtc,
    payload: {
      lastKnownPosition: desk.lastKnownPosition ?? null,
      crewPresence: desk.crewPresence ?? [],
      flightTracks: desk.flightTracks ?? [],
    },
  });
}

export function simulatedConnectSample(sample: PositionSample | null, now = new Date()): FetchSimConnectResult {
  if (!sample) return { ok: false, error: "Simulated flight is not running." };
  const parsed = parsePositionSample({ ...sample, atUtc: now.toISOString() }, now);
  if (!parsed) return { ok: false, error: "Simulated flight sample had no lat/lon." };
  return { ok: true, sample: parsed, transport: "native" };
}

export function createSimFlight(flightId = "c-gnsh-charter", speed = 1): SimFlightState {
  const flight = requireSimFlight(flightId);
  return { flightId: flight.id, speed: clampSimSpeed(speed), paused: false, elapsedMs: 0, lastRealMs: null };
}

export function pauseSimFlight(state: SimFlightState, paused: boolean): SimFlightState {
  return { ...state, paused };
}

export function setSimFlightSpeed(state: SimFlightState, speed: number): SimFlightState {
  return { ...state, speed: clampSimSpeed(speed) };
}

export function jumpSimFlight(state: SimFlightState, phase: SkipPhase): SimFlightState {
  const flight = requireSimFlight(state.flightId);
  const frameAt = flight.frames.find((row) => row.phase === phase);
  if (!frameAt) return state;
  return { ...state, elapsedMs: frameAt.tMin * 60_000, lastRealMs: null, paused: true };
}

/**
 * First call anchors the clock (elapsed stays put). Later calls apply speed.
 * The play head holds on the last frame.
 */
export function advanceSimFlight(state: SimFlightState, nowMs: number): { state: SimFlightState; sample: PositionSample } {
  const flight = requireSimFlight(state.flightId);
  const now = new Date(nowMs);
  if (state.paused) {
    return { state, sample: sampleAt(flight, state.elapsedMs, now) };
  }
  const last = state.lastRealMs ?? nowMs;
  const elapsed = Math.min(simFlightDurationMs(flight), state.elapsedMs + Math.max(0, nowMs - last) * state.speed);
  const next = { ...state, elapsedMs: elapsed, lastRealMs: nowMs };
  return { state: next, sample: sampleAt(flight, elapsed, now) };
}

export function derivedPhase(sample: PositionSample, previous: FlightPhaseId | null, now = new Date(sample.atUtc)): FlightPhaseId {
  return deriveFlightPhase({
    fused: { ...sample, source: "simconnect", stale: false },
    previous,
    now,
    simconnectEnabled: true,
  }).id;
}

export function shiftDepartureIso(departureIso: string, minutes: number): string {
  const next = new Date(Date.parse(departureIso) + minutes * 60_000);
  if (Number.isNaN(next.getTime())) throw new Error("STD is not a valid time.");
  return next.toISOString();
}

/** Mock Megan. Enables the deterministic Grok stand-in and never calls the network. */
export async function meganMockRequest(
  career: { test?: boolean } | null | undefined,
  now = new Date(),
): Promise<CharterRequest> {
  assertTestCareer(career, "Message Megan");
  setMockGrokEnabled(true);
  const fetchImpl = mockGrokFetch();
  const res = await fetchImpl("https://api.x.ai/v1/chat/completions", {
    method: "POST",
    body: JSON.stringify({ messages: [{ role: "user", content: "New charter inquiry for Megan" }] }),
  });
  const json = (await res.json()) as { choices?: { message?: { content?: string } }[] };
  const raw = json.choices?.[0]?.message?.content ?? "";
  const parsed = JSON.parse(raw) as typeof MOCK_GROK_INBOX;
  const row = parsed.inquiries[0];
  if (!row?.clientName) throw new Error("Mock Megan reply was empty.");
  return {
    id: `megan-mock-${now.getTime().toString(36)}`,
    createdAt: now.toISOString(),
    source: "grok",
    status: "new",
    clientName: row.clientName,
    clientEmail: "mara@harborstone.example",
    clientCompany: row.clientCompany,
    originIcao: row.originIcao,
    destIcao: row.destIcao,
    originName: row.originIcao,
    destName: row.destIcao,
    departureIso: row.departureIso,
    paxCount: row.paxCount,
    cargoLb: row.cargoLb,
    reason: row.reason,
    specialRequests: "",
    emailSubject: `Megan mock ${row.originIcao}–${row.destIcao}`,
    emailBody: "Developer mock. No network.",
    suggestedAircraftId: row.suggestedAircraftId,
    flightType: "corporate",
    itineraryKind: "one_way",
  };
}

export type SimFlightSession = { ok: boolean; lines: string[] };

function scheduleKey(state: ReturnType<typeof useDispatchStore.getState>): string {
  return deskScheduleFingerprint({
    missions: state.missions,
    assignments: state.assignments,
    assignmentAudit: state.assignmentAudit,
    company: state.company,
    theme: state.theme,
    themePack: state.themePack,
    themeAccent: state.themeAccent,
    crew: state.crew,
    inbox: state.inbox,
    opsNotamBrief: state.opsNotamBrief,
    duty: state.duty,
  });
}

function liveKey(state: ReturnType<typeof useDispatchStore.getState>): string {
  return liveFingerprint({
    payload: {
      lastKnownPosition: state.lastKnownPosition,
      crewPresence: state.crewPresence,
      flightTracks: state.flightTracks,
    },
  });
}

/**
 * Headless drill. Plays every sample flight through the store pipeline,
 * shifts a TEST trip, files Megan's mock reply, and restores the store.
 * A non-TEST career is refused before any write.
 */
export async function runSimulatedFlightSession(opts?: {
  flightId?: string;
  speed?: number;
  now?: Date;
}): Promise<SimFlightSession> {
  const lines: string[] = [];
  const fail = (message: string): SimFlightSession => ({ ok: false, lines: [...lines, `FAIL ${message}`] });
  const store = useDispatchStore;
  if (!store.persist.hasHydrated()) await store.persist.rehydrate();
  const before = store.getState();
  const snapshot = {
    career: before.career,
    company: before.company,
    missions: before.missions,
    fleet: before.fleet,
    crew: before.crew,
    currentCrewId: before.currentCrewId,
    account: before.account,
    scenery: before.scenery,
    inbox: before.inbox,
    flightLogs: before.flightLogs,
    profile: before.profile,
    lastKnownPosition: before.lastKnownPosition,
    flightTracks: before.flightTracks,
    crewPresence: before.crewPresence,
    notice: before.notice,
    assignments: before.assignments,
  };
  const grokOn = mockGrokEnabled();
  try {
    const real = { career: { test: false }, company: { operatorName: "Northstar" }, missions: before.missions };
    const realBefore = JSON.stringify(real);
    let refused = false;
    try {
      assertSimulatedFlightCareer(real.career);
    } catch (err) {
      refused = err instanceof DeveloperGuardError;
    }
    if (!refused) return fail("a real career was not refused");
    if (JSON.stringify(real) !== realBefore) return fail("the refusal rewrote a real career");
    lines.push("real-career refused");

    const now = opts?.now ?? new Date("2026-11-02T18:00:00.000Z");
    const desk = buildTestCareer(now);
    store.setState({
      ...desk,
      lastKnownPosition: null,
      flightTracks: [],
    });
    assertSimulatedFlightCareer(store.getState().career);
    lines.push("career TEST");

    const speed = clampSimSpeed(opts?.speed ?? 60);
    const primaryId = opts?.flightId ?? "c-gnsh-charter";
    const primary = requireSimFlight(primaryId);
    let state = createSimFlight(primary.id, speed);
    const primed = advanceSimFlight(state, 1_000);
    const moved = advanceSimFlight(primed.state, 2_000);
    state = moved.state;
    if (state.elapsedMs !== 1_000 * speed) return fail(`elapsed ${state.elapsedMs} at x${speed}`);
    lines.push(`flight ${primary.id}`);
    lines.push(`title ${primary.title}`);
    lines.push(`speed x${state.speed}`);

    state = jumpSimFlight(state, "cruise");
    const cruise = sampleAt(primary, state.elapsedMs, now);
    const fused = fuseSimulatedSample(cruise, now);
    if (!fused) return fail("fusion did not take the simulated sample");
    const liveBefore = liveKey(store.getState());
    store.getState().recordFusedPosition(fused);
    const afterFix = store.getState();
    const phone = hydrateFusedPosition(afterFix.lastKnownPosition);
    if (!phone) return fail("companion hydrate dropped the fix");
    if (Math.abs(phone.lat - cruise.lat) > 0.0001) return fail("companion lat");
    if (phone.fuelQtyLb !== cruise.fuelQtyLb) return fail(`companion fuel ${phone.fuelQtyLb}`);
    if (phone.gearDown !== false) return fail("companion gear was not up in cruise");
    if (phone.source !== "simconnect") return fail("companion source");
    if (liveKey(afterFix) === liveBefore) return fail("live fingerprint did not change");
    if ((afterFix.flightTracks?.length ?? 0) < 1) return fail("flight track was not recorded");
    const feed = simConnectFeedFromDesk({
      lastKnownPosition: afterFix.lastKnownPosition,
      crewPresence: afterFix.crewPresence,
      flightTracks: afterFix.flightTracks,
      exportedAtUtc: now.toISOString(),
    });
    const feedFix = hydrateFusedPosition(feed.lastKnownPosition as FusedPosition | null);
    const feedState = aircraftSimState({ fused: feedFix, now, simconnectEnabled: true });
    if (!feedFix || feedFix.source !== "simconnect") return fail("live feed dropped the SimConnect fix");
    if (feedFix.fuelQtyLb !== cruise.fuelQtyLb || feedFix.gearDown !== false) return fail("live feed dropped fuel or gear");
    if (!feedState.live || feedState.phase !== "cruise" || feedState.sample?.gearDown !== false) return fail("live feed phase");
    if (feedState.sample?.fuelQtyLb !== cruise.fuelQtyLb) return fail("live feed sample fuel");
    lines.push("feed simconnect");
    lines.push(`feed phase ${feedState.phase}`);
    lines.push(`phase ${phaseAt(primary, state.elapsedMs)}`);
    lines.push(`fuelLb ${phone.fuelQtyLb}`);
    lines.push(`gear down ${phone.gearDown}`);
    lines.push(`lat ${phone.lat.toFixed(4)}`);
    lines.push(`lon ${phone.lon.toFixed(4)}`);
    lines.push("pipeline native");
    lines.push("companion match");

    const mission = store.getState().missions.find((row) => row.status !== "completed" && row.status !== "cancelled");
    if (!mission?.departureIso) return fail("no open TEST trip");
    const stdBefore = mission.departureIso;
    const scheduleBefore = scheduleKey(store.getState());
    const shifted = shiftDepartureIso(stdBefore, 45);
    const latHeld = store.getState().lastKnownPosition?.lat;
    store.getState().updateMissionDeparture(mission.id, shifted);
    const shiftedState = store.getState();
    const stdAfter = shiftedState.missions.find((row) => row.id === mission.id)?.departureIso;
    if (stdAfter !== shifted) return fail("STD did not move");
    if (shiftedState.lastKnownPosition?.lat !== latHeld) return fail("schedule shift cleared the live position");
    if (shiftedState.lastKnownPosition?.fuelQtyLb !== phone.fuelQtyLb) return fail("schedule shift cleared fuel");
    if (scheduleKey(shiftedState) === scheduleBefore) return fail("schedule fingerprint did not change");
    lines.push(`std ${stdBefore} -> ${stdAfter}`);
    lines.push("position held");

    const originalFetch = globalThis.fetch;
    let network = 0;
    globalThis.fetch = (() => {
      network += 1;
      throw new Error("network");
    }) as typeof fetch;
    try {
      const request = await meganMockRequest(shiftedState.career, now);
      if (request.clientName !== "Mara Keating") return fail(`Megan reply ${request.clientName}`);
      if (network !== 0) return fail("Megan mock used the network");
      const inboxBefore = shiftedState.inbox.length;
      store.getState().addInboxRequests([request], { file: false });
      const filed = store.getState().inbox[0];
      if (store.getState().inbox.length < inboxBefore + 1) return fail("inbox did not grow");
      if (filed?.clientName !== "Mara Keating") return fail("inbox lost Megan's client");
      if (store.getState().lastKnownPosition?.lat !== latHeld) return fail("Megan reply cleared the live position");
      lines.push(`megan ${filed.clientName} ${request.originIcao}-${request.destIcao}`);
    } finally {
      globalThis.fetch = originalFetch;
    }

    const delay = requireSimFlight("c-gnsh-delay");
    const delaySample = sampleAt(delay, 10 * 60_000, now);
    if (phaseAt(delay, 10 * 60_000) !== "preflight") return fail("delay was not holding on the ramp");
    if (delaySample.onGround !== true || delaySample.gearDown !== true) return fail("delay gear");
    if (Math.abs(delaySample.lat - CYPD.lat) > 0.02) return fail("delay left CYPD");
    lines.push("delay ramp hold");

    const ferry = sampleAt(requireSimFlight("c-gnsh-ferry"), 0, now);
    if (Math.abs(ferry.lat - CYUL.lat) > 0.02) return fail("ferry did not start at CYUL");
    lines.push("ferry CYUL");

    const diversion = requireSimFlight("c-gnsh-diversion");
    const end = sampleAt(diversion, simFlightDurationMs(diversion), now);
    const toHz = Math.hypot(end.lat - CYHZ.lat, end.lon - CYHZ.lon);
    const toUl = Math.hypot(end.lat - CYUL.lat, end.lon - CYUL.lon);
    if (!(toHz < toUl)) return fail("diversion did not land at CYHZ");
    lines.push("diversion CYHZ");

    const tokenParked = liveFixToken({ ...fused, lat: CYPD.lat, lon: CYPD.lon, onGround: true, gearDown: true, fuelQtyLb: 3200 });
    const tokenGear = liveFixToken({ ...fused, lat: CYPD.lat, lon: CYPD.lon, onGround: true, gearDown: false, fuelQtyLb: 3200 });
    const tokenFuel = liveFixToken({ ...fused, lat: CYPD.lat, lon: CYPD.lon, onGround: true, gearDown: true, fuelQtyLb: 3188 });
    if (tokenParked === tokenGear || tokenParked === tokenFuel) return fail("live token ignored gear or fuel");
    lines.push("PASS");
    return { ok: true, lines };
  } catch (err) {
    return fail(err instanceof Error ? err.message : String(err));
  } finally {
    store.setState(snapshot);
    setMockGrokEnabled(grokOn);
  }
}
